import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from "react";
import { useQuery } from "react-query";

import {
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Button,
  Divider as MuiDivider,
  InputAdornment,
  Paper,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { Search as SearchIcon, GetApp as GetAppIcon } from "@material-ui/icons";
import { Alert } from "@material-ui/lab";

import styled from "styled-components/macro";
import { spacing } from "@material-ui/system";
import Link from "@material-ui/core/Link";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { useAuth0 } from "@auth0/auth0-react";
import useFetchData from "../../../../hooks/useFetchData";
import Loader from "../../../../components/Loader";
import { formatter } from "../../../../utils";
import { generateCSV } from "../../../dashboards/Default/utils";
import ColumnFilter from "../MeterReadingsManagement/ColumnFilter";

const Divider = styled(MuiDivider)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const StyledTableContainer = styled(TableContainer)`
  max-height: calc(100vh - 400px);
  overflow: auto;
`;

const Cell = styled(TableCell)`
  padding: ${(props) => props.theme.spacing(1)}px;
  font-size: 0.75rem;
  white-space: nowrap;
  border: 1px solid #eee;
`;

const HeaderCell = styled(TableCell)`
  font-weight: 600;
  font-size: 0.75rem;
  white-space: nowrap;
  background-color: ${(props) => props.theme.palette.background.default};
  padding: ${(props) => props.theme.spacing(1)}px;
  border: 1px solid #eee;
  position: sticky;
  top: 0;
  z-index: 101;
`;

const DataRow = styled(TableRow)`
  cursor: pointer;
  &:hover td {
    background-color: ${(props) => props.theme.palette.grey[100]};
  }
`;

const SelectedDataRow = styled(TableRow)`
  cursor: pointer;
  & td {
    background-color: #fffde7;
  }
  &:hover td {
    background-color: #fff9c4;
  }
`;

const StyledLink = styled.a`
  color: #1976d2;
  text-decoration: underline;
  cursor: pointer;
  &:hover {
    color: #1565c0;
  }
`;

const MoreButton = styled.button`
  display: block;
  color: ${(props) => props.theme.palette.secondary.main};
  background: none;
  border: 1px solid ${(props) => props.theme.palette.secondary.main};
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.65rem;
  font-weight: 600;
  padding: 1px 6px;
  margin-top: 3px;
  &:hover {
    background-color: ${(props) => props.theme.palette.secondary.main};
    color: #fff;
  }
`;

const DetailHeaderCell = styled(TableCell)`
  font-weight: 600;
  font-size: 0.75rem;
  white-space: nowrap;
  background-color: ${(props) => props.theme.palette.background.default};
  padding: ${(props) => props.theme.spacing(1)}px;
`;

const MONTH_NAMES = {
  1: "January",
  2: "February",
  3: "March",
  4: "April",
  5: "May",
  6: "June",
  7: "July",
  8: "August",
  9: "September",
  10: "October",
  11: "November",
  12: "December",
};

const fiscalYearLabel = (fy) => `${fy - 1}\u2013${fy}`;

const SUMMARY_COLUMNS = [
  { id: "has_negative", label: "<0 Pumping", align: "center" },
  {
    id: "grouping_name",
    label: "Owner/Permit Name",
    align: "left",
    filterable: true,
  },
  {
    id: "fiscal_year",
    label: "Fiscal Year (Sep\u2013Aug)",
    align: "left",
    filterable: true,
  },
  { id: "last_report", label: "Last Report", align: "left" },
  { id: "permitted_gallons", label: "Permitted Gallons", align: "right" },
  { id: "metered_gallons", label: "YTD All Meters", align: "right" },
  { id: "remaining_gallons", label: "FY Remaining", align: "right" },
  { id: "pct_pumped", label: "% Pumped", align: "right" },
  { id: "is_over80pct", label: "Over 80%", align: "center" },
  { id: "is_overpumped", label: "Over Allocation", align: "center" },
  {
    id: "grouped_permits",
    label: "Permits",
    align: "left",
    minWidth: 160,
  },
];

const DETAIL_COLUMNS = [
  { id: "grouping_name", label: "Owner/Permit Name", align: "left" },
  { id: "c_year", label: "Cal Year", align: "left" },
  { id: "c_month", label: "Month", align: "left" },
  { id: "permitted_gallons", label: "Permitted Gallons", align: "right" },
  { id: "metered_gallons", label: "Metered", align: "right" },
  {
    id: "cumulative_metered_gallons",
    label: "YTD Metered",
    align: "right",
  },
  { id: "pct_pumped", label: "% Pumped", align: "right" },
  {
    id: "reduced_annual_volume",
    label: "Reduced Annual Permitted",
    align: "right",
  },
  {
    id: "pct_reduced_pumped",
    label: "% of Reduced Annual",
    align: "right",
  },
];

const ARRAY_COL_INLINE_LIMIT = 2;

const MeterReadingsTrackingHistory = () => {
  const { getAccessTokenSilently } = useAuth0();
  // ── Data: pumping summary (all fiscal years) ──
  const {
    data: summaryData,
    isLoading: isLoadingSummary,
    error: errorSummary,
  } = useQuery(
    ["ui-pumping-summary-historical-live"],
    async () => {
      try {
        const token = await getAccessTokenSilently();
        const headers = { Authorization: `Bearer ${token}` };
        const { data } = await axios.get(
          `${process.env.REACT_APP_ENDPOINT}/api/ui-pumping-summary-historical-live`,
          { headers }
        );
        return data;
      } catch (err) {
        console.error(err);
      }
    },
    { keepPreviousData: true, refetchOnWindowFocus: false }
  );

  // ── Data: tracking detail (all fiscal years) ──
  const { data: detailData, isLoading: isLoadingDetail } = useQuery(
    ["ui-meter-readings-tracking-detail-historical"],
    async () => {
      try {
        const token = await getAccessTokenSilently();
        const headers = { Authorization: `Bearer ${token}` };
        const { data } = await axios.get(
          `${process.env.REACT_APP_ENDPOINT}/api/ui-meter-readings-tracking-detail-historical`,
          { headers }
        );
        return data;
      } catch (err) {
        console.error(err);
      }
    },
    { keepPreviousData: true, refetchOnWindowFocus: false }
  );

  // ── Lookups ──
  const [permitsLookup] = useFetchData("ui-list-permits", [], true);
  const permitsMap = useMemo(() => {
    const m = {};
    if (permitsLookup?.length > 0) {
      permitsLookup.forEach((item) => {
        m[item.permit_ndx] = item.permit_number_only;
      });
    }
    return m;
  }, [permitsLookup]);

  // ── Column filters ──
  const [columnFilters, setColumnFilters] = useState({});
  const [searchText, setSearchText] = useState("");
  const [orderBy, setOrderBy] = useState("fiscal_year");
  const [order, setOrder] = useState("desc");

  const filterOptions = useMemo(() => {
    if (!summaryData?.length) return {};
    const nameSet = new Set();
    const fySet = new Set();
    summaryData.forEach((row) => {
      if (row.grouping_name) nameSet.add(row.grouping_name);
      if (row.fiscal_year != null) fySet.add(fiscalYearLabel(row.fiscal_year));
    });
    return {
      grouping_name: [...nameSet].sort(),
      fiscal_year: [...fySet].sort().reverse(),
    };
  }, [summaryData]);

  const filtersInitialized = useRef(false);
  useEffect(() => {
    if (filterOptions.grouping_name?.length && !filtersInitialized.current) {
      filtersInitialized.current = true;
      const today = new Date();
      const currentFY =
        today.getMonth() >= 8 ? today.getFullYear() + 1 : today.getFullYear();
      setColumnFilters({
        grouping_name: [...filterOptions.grouping_name],
        fiscal_year: filterOptions.fiscal_year.filter((label) => {
          const fy = parseInt(label.split("\u2013")[1], 10);
          return fy <= currentFY;
        }),
      });
    }
  }, [filterOptions]);

  const handleFilterChange = useCallback((colId, values) => {
    setColumnFilters((prev) => ({ ...prev, [colId]: values }));
  }, []);

  const handleSort = useCallback(
    (colId) => {
      if (orderBy === colId) {
        setOrder((prev) => (prev === "asc" ? "desc" : "asc"));
      } else {
        setOrderBy(colId);
        setOrder("asc");
      }
    },
    [orderBy]
  );

  // ── Filter & sort summary ──
  const filteredSummaryRows = useMemo(() => {
    if (!summaryData?.length) return [];
    let rows = summaryData;

    if (
      columnFilters.grouping_name?.length > 0 &&
      columnFilters.grouping_name.length <
        (filterOptions.grouping_name?.length || 0)
    ) {
      const s = new Set(columnFilters.grouping_name);
      rows = rows.filter((r) => s.has(r.grouping_name));
    }
    if (
      columnFilters.fiscal_year?.length > 0 &&
      columnFilters.fiscal_year.length <
        (filterOptions.fiscal_year?.length || 0)
    ) {
      const s = new Set(columnFilters.fiscal_year);
      rows = rows.filter((r) => s.has(fiscalYearLabel(r.fiscal_year)));
    }

    if (searchText.trim()) {
      const term = searchText.trim().toLowerCase();
      rows = rows.filter((row) => {
        const vals = [
          row.grouping_name,
          fiscalYearLabel(row.fiscal_year),
          row.last_report,
          row.permitted_gallons != null ? String(row.permitted_gallons) : "",
          row.metered_gallons != null ? String(row.metered_gallons) : "",
          (row.grouped_permits || [])
            .map((ndx) => permitsMap[ndx] || "")
            .join(" "),
        ];
        return vals.some((v) => v && v.toLowerCase().includes(term));
      });
    }

    const sorted = [...rows].sort((a, b) => {
      let aVal = a[orderBy];
      let bVal = b[orderBy];
      if (typeof aVal === "string") {
        aVal = aVal?.toLowerCase() || "";
        bVal = bVal?.toLowerCase() || "";
      }
      if (aVal < bVal) return order === "asc" ? -1 : 1;
      if (aVal > bVal) return order === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [
    summaryData,
    columnFilters,
    filterOptions,
    searchText,
    orderBy,
    order,
    permitsMap,
  ]);

  // ── Pagination ──
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(30);

  useEffect(() => {
    setPage(0);
  }, [columnFilters, searchText]);

  const paginatedSummaryRows = useMemo(
    () => filteredSummaryRows.slice(page * perPage, page * perPage + perPage),
    [filteredSummaryRows, page, perPage]
  );

  // ── Row selection ──
  const [selectedKey, setSelectedKey] = useState(null);

  const handleRowClick = (row) => {
    const key = `${row.grouping_ndx}-${row.fiscal_year}`;
    setSelectedKey((prev) => (prev === key ? null : key));
  };

  const selectedSummaryRow = useMemo(() => {
    if (!selectedKey) return null;
    const [ndx, fy] = selectedKey.split("-").map(Number);
    return filteredSummaryRows.find(
      (r) => r.grouping_ndx === ndx && r.fiscal_year === fy
    );
  }, [selectedKey, filteredSummaryRows]);

  // ── Filter detail for selected summary row ──
  const filteredDetailRows = useMemo(() => {
    if (!selectedSummaryRow || !detailData?.length) return [];
    return detailData
      .filter(
        (r) =>
          r.grouping_ndx === selectedSummaryRow.grouping_ndx &&
          r.fiscal_year === selectedSummaryRow.fiscal_year
      )
      .sort((a, b) => {
        if (a.c_year !== b.c_year) return b.c_year - a.c_year;
        return b.c_month - a.c_month;
      });
  }, [selectedSummaryRow, detailData]);

  // ── CSV export ──
  const handleExportCSV = useCallback(() => {
    if (!filteredSummaryRows?.length) return;
    const headers = [
      "<0 Pumping",
      "Owner/Permit Name",
      "Fiscal Year",
      "Last Report",
      "Permitted Gallons",
      "YTD All Meters",
      "FY Remaining",
      "% Pumped",
      "Over 80%",
      "Over Allocation",
      "Permits",
    ];
    const dataRows = filteredSummaryRows.map((row) => [
      row.has_negative ? "Yes" : "No",
      row.grouping_name,
      fiscalYearLabel(row.fiscal_year),
      row.last_report || "",
      row.permitted_gallons,
      row.metered_gallons,
      row.remaining_gallons,
      row.pct_pumped != null ? (row.pct_pumped * 100).toFixed(2) + "%" : "N/A",
      row.is_over80pct ? "Yes" : "No",
      row.is_overpumped ? "Yes" : "No",
      (row.grouped_permits || [])
        .map((ndx) => permitsMap[ndx] || ndx)
        .join("; "),
    ]);
    generateCSV(headers, dataRows, "historical-tracking-summary.csv");
  }, [filteredSummaryRows, permitsMap]);

  // ── Popover ──
  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const [popoverLinks, setPopoverLinks] = useState([]);

  const openPopover = (event, links) => {
    event.stopPropagation();
    setPopoverAnchor(event.currentTarget);
    setPopoverLinks(links);
  };

  const closePopover = () => {
    setPopoverAnchor(null);
    setPopoverLinks([]);
  };

  // ── Render helpers ──
  const stopProp = (e) => e.stopPropagation();

  const renderGroupingLink = (row) => {
    const ndx = row.grouping_ndx;
    const isGroup = ndx >= 100000;
    const path = isGroup
      ? `/data-management/permit-records/permit-groups-management/${
          ndx - 100000
        }`
      : `/data-management/permit-records/permits-management/${ndx}`;
    return (
      <StyledLink
        href={path}
        target="_blank"
        rel="noopener noreferrer"
        onClick={stopProp}
      >
        {row.grouping_name}
      </StyledLink>
    );
  };

  const renderPermitLinks = (grouped_permits) => {
    if (!grouped_permits?.length) return null;
    const unique = [
      ...new Set(grouped_permits.filter((ndx) => permitsMap[ndx])),
    ];
    const links = unique.map((ndx) => (
      <StyledLink
        key={ndx}
        href={`/data-management/permit-records/permits-management/${ndx}`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={stopProp}
      >
        {permitsMap[ndx]}
      </StyledLink>
    ));
    if (links.length <= ARRAY_COL_INLINE_LIMIT) {
      return links.map((link, i) => (
        <span key={link.key}>
          {link}
          {i < links.length - 1 ? "; " : ""}
        </span>
      ));
    }
    const inline = links.slice(0, ARRAY_COL_INLINE_LIMIT);
    return (
      <>
        {inline.map((link, i) => (
          <span key={link.key}>
            {link}
            {i < inline.length - 1 ? "; " : ""}
          </span>
        ))}
        <MoreButton onClick={(e) => openPopover(e, links)}>
          +{links.length - ARRAY_COL_INLINE_LIMIT} more
        </MoreButton>
      </>
    );
  };

  const renderSummaryCell = (row, colId) => {
    switch (colId) {
      case "has_negative":
        return row.has_negative ? "Yes" : "No";
      case "grouping_name":
        return renderGroupingLink(row);
      case "fiscal_year":
        return fiscalYearLabel(row.fiscal_year);
      case "last_report":
        return row.last_report || "";
      case "permitted_gallons":
        return row.permitted_gallons != null
          ? formatter(row.permitted_gallons, 0)
          : "N/A";
      case "metered_gallons":
        return formatter(row.metered_gallons, 0) ?? "0";
      case "remaining_gallons":
        return row.remaining_gallons != null
          ? formatter(row.remaining_gallons, 0)
          : "N/A";
      case "pct_pumped":
        return row.pct_pumped != null
          ? `${(row.pct_pumped * 100).toFixed(1)}%`
          : "N/A";
      case "is_over80pct":
        return row.is_over80pct === true
          ? "Yes"
          : row.is_over80pct === false
          ? "No"
          : "N/A";
      case "is_overpumped":
        return row.is_overpumped === true
          ? "Yes"
          : row.is_overpumped === false
          ? "No"
          : "N/A";
      case "grouped_permits":
        return renderPermitLinks(row.grouped_permits);
      default:
        return row[colId];
    }
  };

  const getSummaryCellStyle = (row, colId) => {
    if (colId === "has_negative" && row.has_negative) {
      return { backgroundColor: "red", color: "white", fontWeight: 600 };
    }
    if (colId === "is_overpumped" && row.is_overpumped) {
      return {
        backgroundColor: "orange",
        color: "white",
        fontWeight: 600,
      };
    }
    if (colId === "is_over80pct" && row.is_over80pct) {
      return { backgroundColor: "yellow", fontWeight: 600 };
    }
    return undefined;
  };

  const renderDetailCell = (row, colId) => {
    switch (colId) {
      case "grouping_name":
        return renderGroupingLink(row);
      case "c_year":
        return row.c_year;
      case "c_month":
        return MONTH_NAMES[row.c_month] || row.c_month;
      case "permitted_gallons":
        return row.permitted_gallons != null
          ? formatter(row.permitted_gallons, 0)
          : "N/A";
      case "metered_gallons":
        return formatter(row.metered_gallons, 0) ?? "0";
      case "cumulative_metered_gallons":
        return formatter(row.cumulative_metered_gallons, 0) ?? "0";
      case "pct_pumped":
        return row.pct_pumped != null
          ? `${(row.pct_pumped * 100).toFixed(1)}%`
          : "N/A";
      case "reduced_annual_volume":
        return row.reduced_annual_volume != null
          ? formatter(row.reduced_annual_volume, 0)
          : "N/A";
      case "pct_reduced_pumped":
        return row.pct_reduced_pumped != null
          ? `${(row.pct_reduced_pumped * 100).toFixed(1)}%`
          : "N/A";
      default:
        return row[colId];
    }
  };

  const getDetailCellStyle = (row, colId) => {
    if (colId === "pct_pumped" && row.pct_pumped != null) {
      if (row.pct_pumped >= 1)
        return {
          backgroundColor: "orange",
          color: "white",
          fontWeight: 600,
        };
      if (row.pct_pumped >= 0.8)
        return { backgroundColor: "yellow", fontWeight: 600 };
    }
    if (
      colId === "pct_reduced_pumped" &&
      row.pct_reduced_pumped != null &&
      row.pct_reduced_pumped > 1
    ) {
      return {
        backgroundColor: "orange",
        color: "white",
        fontWeight: 600,
      };
    }
    return undefined;
  };

  if (errorSummary) return "An error has occurred: " + errorSummary.message;

  return (
    <>
      <Helmet title="Historical Meter Readings Tracking" />
      <Typography variant="h3" gutterBottom display="inline">
        Historical Meter Readings Tracking
      </Typography>

      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link component={NavLink} exact to="/dashboard">
          Dashboard
        </Link>
        <Typography>Historical Meter Readings Tracking</Typography>
      </Breadcrumbs>

      <Divider my={6} />

      {!summaryData || isLoadingSummary ? (
        <Loader />
      ) : (
        <>
          <Alert severity="info" style={{ marginBottom: 12 }}>
            Click a row to view the monthly pumping detail for that permit
            owner/group and fiscal year. Pumping summaries are only available
            from approximately 2015 onward due to limited permitted volume
            records for earlier years.
          </Alert>

          {/* ── Detail Table (shown when summary row selected) ── */}
          {selectedSummaryRow && (
            <TableContainer
              component={Paper}
              style={{
                marginBottom: 16,
                border: "2px solid #ccc",
                borderRadius: 4,
                maxHeight: 400,
                overflowY: "auto",
              }}
            >
              <Box px={2} pt={1.5} pb={0.5}>
                <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                  Monthly Pumping Detail &mdash;{" "}
                  {selectedSummaryRow.grouping_name}, FY{" "}
                  {fiscalYearLabel(selectedSummaryRow.fiscal_year)}
                </Typography>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  style={{ display: "block", marginBottom: 4 }}
                >
                  {filteredDetailRows.length} month
                  {filteredDetailRows.length !== 1 ? "s" : ""} of data
                  {isLoadingDetail && " (loading...)"}
                </Typography>
              </Box>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {DETAIL_COLUMNS.map((col) => (
                      <DetailHeaderCell key={col.id} align={col.align}>
                        {col.label}
                      </DetailHeaderCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredDetailRows.map((row) => (
                    <TableRow key={`${row.c_year}-${row.c_month}`}>
                      {DETAIL_COLUMNS.map((col) => (
                        <Cell
                          key={col.id}
                          align={col.align}
                          style={getDetailCellStyle(row, col.id)}
                        >
                          {renderDetailCell(row, col.id)}
                        </Cell>
                      ))}
                    </TableRow>
                  ))}
                  {filteredDetailRows.length === 0 && !isLoadingDetail && (
                    <TableRow>
                      <Cell colSpan={DETAIL_COLUMNS.length} align="center">
                        <Typography variant="body2" color="textSecondary">
                          No monthly detail available for this selection.
                        </Typography>
                      </Cell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* ── Summary Table ── */}
          <Paper>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              px={2}
              pt={1.5}
            >
              <Typography variant="subtitle2" color="textSecondary">
                Showing {filteredSummaryRows.length.toLocaleString()} of{" "}
                {(summaryData?.length || 0).toLocaleString()} records
              </Typography>
              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                <Tooltip title="Export filtered rows to CSV">
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleExportCSV}
                    startIcon={<GetAppIcon style={{ fontSize: 16 }} />}
                    style={{ fontSize: "0.7rem", padding: "2px 10px" }}
                  >
                    CSV
                  </Button>
                </Tooltip>
                <TextField
                  size="small"
                  variant="outlined"
                  placeholder="Search..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon style={{ fontSize: 18 }} color="action" />
                      </InputAdornment>
                    ),
                    style: { fontSize: "0.8rem" },
                  }}
                  style={{ width: 220 }}
                />
              </Box>
            </Box>
            <StyledTableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {SUMMARY_COLUMNS.map((col) => (
                      <HeaderCell key={col.id} align={col.align}>
                        <Box display="flex" alignItems="center">
                          <TableSortLabel
                            active={orderBy === col.id}
                            direction={orderBy === col.id ? order : "asc"}
                            onClick={() => handleSort(col.id)}
                          >
                            {col.label}
                          </TableSortLabel>
                          {col.filterable &&
                            filterOptions[col.id]?.length > 0 && (
                              <ColumnFilter
                                label={col.label}
                                options={filterOptions[col.id]}
                                selectedValues={columnFilters[col.id] || []}
                                onChange={(values) =>
                                  handleFilterChange(col.id, values)
                                }
                              />
                            )}
                        </Box>
                      </HeaderCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedSummaryRows.map((row) => {
                    const key = `${row.grouping_ndx}-${row.fiscal_year}`;
                    const isSelected = selectedKey === key;
                    const RowComponent = isSelected ? SelectedDataRow : DataRow;
                    return (
                      <RowComponent
                        key={key}
                        onClick={() => handleRowClick(row)}
                      >
                        {SUMMARY_COLUMNS.map((col) => (
                          <Cell
                            key={col.id}
                            align={col.align}
                            style={{
                              ...getSummaryCellStyle(row, col.id),
                              ...(col.minWidth
                                ? { minWidth: col.minWidth }
                                : {}),
                            }}
                          >
                            {renderSummaryCell(row, col.id)}
                          </Cell>
                        ))}
                      </RowComponent>
                    );
                  })}
                  {paginatedSummaryRows.length === 0 && (
                    <TableRow>
                      <Cell colSpan={SUMMARY_COLUMNS.length} align="center">
                        <Typography variant="body2" color="textSecondary">
                          No records match the current filters.
                        </Typography>
                      </Cell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </StyledTableContainer>
            <TablePagination
              component="div"
              count={filteredSummaryRows.length}
              page={page}
              onChangePage={(e, p) => setPage(p)}
              rowsPerPage={perPage}
              onChangeRowsPerPage={(e) => {
                setPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[30, 60, 100, 200]}
            />
          </Paper>
        </>
      )}
      <Popover
        open={Boolean(popoverAnchor)}
        anchorEl={popoverAnchor}
        onClose={closePopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          style: {
            border: "1px solid #ccc",
            borderRadius: 6,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            minWidth: 200,
            maxWidth: 320,
            maxHeight: 300,
            overflowY: "auto",
          },
        }}
      >
        <Box px={2} py={1.5}>
          <Typography
            variant="caption"
            color="textSecondary"
            style={{ fontWeight: 600, marginBottom: 6, display: "block" }}
          >
            {popoverLinks.length} permits
          </Typography>
          {popoverLinks.map((link) => (
            <Box
              key={link.key}
              py={0.5}
              style={{
                fontSize: "0.8rem",
                borderBottom: "1px solid #f0f0f0",
              }}
            >
              {link}
            </Box>
          ))}
        </Box>
      </Popover>
    </>
  );
};

export default MeterReadingsTrackingHistory;
